import React, { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users } from "lucide-react";
import { formatCHPhone, toTelHref, toMailHref } from "../../utils/phone.js";
import { useAuth } from "../ui/AuthContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import { canAccessWartung, canEnterHoursUnlimited, canManageCustomers } from "../../constants/roles.js";
import AppButton from "../ui/AppButton.jsx";
import EntityCreateMenu from "../ui/EntityCreateMenu.jsx";
import { Field } from "../forms/shared/FormAtoms.jsx";
import {
  detailPageHeaderNavSecondaryShellClassName,
  entityInfoBlockEditButtonClassName,
  entityInfoBlockHeaderActionsClassName,
  entityInfoBlockHeaderShellClassName,
  entityRowDeleteActionClassName,
} from "../../design/tokens.js";
import DetailMapsRouteLink from "../ui/DetailMapsRouteLink.jsx";
import EntityInlineFileLinks from "../ui/EntityInlineFileLinks.jsx";
import { buildKundenInlineFiles } from "../../utils/entityInlineFilePresets.js";
import {
  confirmKundeBonitaetBeforeCreate,
  getEntityCreateMenuItems,
  navigateEntityCreate,
} from "../../utils/entityCreateMenu.js";

const mapBonitaet = (kunde) => {
  const v = Number(kunde?.bonitaet_status ?? 1);
  if (v === 3) {
    return {
      label: "Bonität: Rot – gesperrt",
      short: "Rot",
      color: "bg-red-500/15 text-red-200 border-red-400/40",
    };
  }
  if (v === 2) {
    return {
      label: "Bonität: Gelb – Vorsicht",
      short: "Gelb",
      color: "bg-amber-500/15 text-amber-200 border-amber-400/40",
    };
  }
  return {
    label: "Bonität: Grün – OK",
    short: "Grün",
    color: "bg-green-500/15 text-green-200 border-green-400/40",
  };
};

function KundenBlockAdresse({ kunde, onBearbeiten, onLoeschen }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  const { user: authUser } = useAuth();
  const darfVerwalten = canManageCustomers(authUser);
  const darfLoeschen = darfVerwalten;
  const darfErstellen = darfVerwalten;
  const darfWartung = canAccessWartung(authUser);

  if (!kunde) return null;

  const firma = kunde.firma || "";
  const vorname = kunde.vorname || "";
  const nachname = kunde.nachname || "";
  const strasse = kunde.strasse || "";
  const plz = kunde.plz || "";
  const ort = kunde.ort || "";
  const tel1Nice = formatCHPhone((kunde.telefon || "").trim());
  const tel2Nice = formatCHPhone((kunde.telefon2 || "").trim());
  const emailValue = (kunde.email || "").trim();
  const plzOrtDisplay = [plz, ort].filter(Boolean).join(" ") || "–";
  const addressParts = [strasse, [plz, ort].filter(Boolean).join(" ")].filter(Boolean);
  const fullAddress = addressParts.join(", ");
  const boni = mapBonitaet(kunde);
  const kundeId = Number(kunde.id || kunde.kunden_id || 0);

  const goCreate = async (target) => {
    const ok = await confirmKundeBonitaetBeforeCreate(confirm, kunde?.bonitaet_status);
    if (!ok) return;
    navigateEntityCreate(navigate, target, { kundeId });
  };

  const createMenuItems = getEntityCreateMenuItems({ showWartung: darfWartung });

  return (
    <section className={`rounded-2xl px-3 py-3 border border-default bg-surface text-primary app-frame ${createMenuOpen ? "relative z-[1300]" : ""}`}>
      <div className={entityInfoBlockHeaderShellClassName}>
        <h2 className="text-lg sm:text-xl font-semibold flex items-start gap-2 min-w-0 max-md:text-base max-md:leading-snug sm:min-w-[12rem] sm:flex-1 text-blue-100/90">
          <Users className="w-5 h-5 shrink-0 mt-0.5 max-md:w-4 max-md:h-4" />
          <span className="min-w-0 flex flex-wrap items-center gap-1.5 sm:gap-2">
            <span className="line-clamp-2">Kundendaten</span>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] sm:text-xs font-medium shrink-0 ${boni.color}`}
              title={kunde.bonitaet_notiz || boni.label}
            >
              <span className="inline-block h-2 w-2 rounded-full bg-current" aria-hidden />
              {boni.short}
            </span>
          </span>
        </h2>

        <div className={entityInfoBlockHeaderActionsClassName}>
          {darfErstellen ? (
            <EntityCreateMenu
              items={createMenuItems}
              onSelect={goCreate}
              title="Neu aus diesem Kunden"
              onOpenChange={setCreateMenuOpen}
            />
          ) : null}

          {darfVerwalten && (
            <AppButton
              type="button"
              variant="secondary"
              size="sm"
              className={`${entityInfoBlockEditButtonClassName} ${detailPageHeaderNavSecondaryShellClassName}`}
              onClick={() => {
                if (typeof onBearbeiten === "function") return onBearbeiten(kunde);
                navigate(`/kunden/${kunde.id}/bearbeiten`);
              }}
              title="Bearbeiten"
              aria-label="Bearbeiten"
            >
              Bearbeiten
            </AppButton>
          )}

          {darfLoeschen && (
            <AppButton
              type="button"
              variant="secondary"
              size="sm"
              className={`${entityInfoBlockEditButtonClassName} ${entityRowDeleteActionClassName}`}
              onClick={() => onLoeschen?.()}
              title="Löschen"
              aria-label="Löschen"
            >
              Löschen
            </AppButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Firma" value={firma || "–"} />
        <Field label="Vorname" value={vorname || "–"} />
        <Field label="Nachname" value={nachname || "–"} />
        <Field label="Adresse" value={strasse || "–"} />
        <Field label="PLZ / Ort" value={plzOrtDisplay} />
        <Field
          label="Telefon"
          value={
            tel1Nice ? (
              <a href={toTelHref(kunde.telefon)} className="underline underline-offset-2 hover:text-blue-200 tabular-nums">
                {tel1Nice}
              </a>
            ) : (
              "–"
            )
          }
        />
        <Field
          label="E-Mail"
          value={
            emailValue ? (
              <a href={toMailHref(emailValue)} className="underline underline-offset-2 hover:text-blue-200 break-all">
                {emailValue}
              </a>
            ) : (
              "–"
            )
          }
        />
        <Field
          label="Telefon 2"
          value={
            tel2Nice ? (
              <a href={toTelHref(kunde.telefon2)} className="underline underline-offset-2 hover:text-blue-200 tabular-nums">
                {tel2Nice}
              </a>
            ) : (
              "–"
            )
          }
        />
      </div>

      {kunde?.id ? (
        <EntityInlineFileLinks
          {...(buildKundenInlineFiles(kunde.id) || {})}
          documentsSectionLabel="Kundendokumente"
        />
      ) : null}

      {fullAddress ? <DetailMapsRouteLink address={fullAddress} /> : null}
    </section>
  );
}

export default memo(KundenBlockAdresse);
